/**
 * @fileoverview Repositorio especializado para la gestión de tiendaId
 * Implementa patrón Repository y principio de Responsabilidad Única
 */

class TiendaIdRepository {
  /**
   * Constructor del repositorio
   * @param {Object} logger - Logger para diagnóstico y seguimiento
   */
  constructor(logger) {
    this.logger = logger || console;
    this.STORAGE_KEYS = {
      LOCAL: {
        TIENDA_ID: 'tiendaId',
        TIENDA_ID_ALT: 'tienda_id',
        TIMESTAMP: 'tiendaId_timestamp',
        SOURCE: 'tiendaId_source'
      },
      SESSION: {
        TIENDA_ID: 'tiendaId',
        TIENDA_ID_ALT: 'tienda_id',
        LAST_TIENDA_ID: 'lastTiendaId'
      }
    };
  }

  /**
   * Recupera tiendaId usando una estrategia optimizada de múltiples fuentes
   * @param {Object} user - Usuario Firebase autenticado
   * @param {string} token - Token JWT o Firebase
   * @returns {Promise<{tiendaId: string|null, source: string|null}>} tiendaId y su fuente
   */
  async obtenerTiendaId(user, token) {
    let tiendaId = null;
    let source = null;

    // 1. PRIMERA PRIORIDAD: Parámetros de URL (son los más recientes y provienen del callback)
    const urlResult = this._obtenerDesdeUrl();
    if (urlResult.tiendaId) {
      tiendaId = urlResult.tiendaId;
      source = 'url_params';
    }

    // 2. SEGUNDA PRIORIDAD: sessionStorage
    if (!tiendaId) {
      const sessionResult = this._obtenerDesdeSessionStorage();
      if (sessionResult.tiendaId) {
        tiendaId = sessionResult.tiendaId;
        source = 'session_storage';
      }
    }

    // 3. TERCERA PRIORIDAD: localStorage
    if (!tiendaId) {
      const localResult = this._obtenerDesdeLocalStorage();
      if (localResult.tiendaId) {
        tiendaId = localResult.tiendaId;
        source = 'local_storage';
      }
    }

    // 4. CUARTA PRIORIDAD: Token JWT
    if (!tiendaId && token) {
      const jwtResult = this._extraerDesdeTiendaIdToken(token);
      if (jwtResult.tiendaId) {
        tiendaId = jwtResult.tiendaId;
        source = 'jwt_token';
      }
    }

    // 5. QUINTA PRIORIDAD: Firebase User Claims
    if (!tiendaId && user) {
      const claimsResult = await this._extraerDesdeClaims(user);
      if (claimsResult.tiendaId) {
        tiendaId = claimsResult.tiendaId;
        source = 'firebase_claims';
      }
    }

    // 6. SEXTA PRIORIDAD: Fallback al UID de Firebase
    if (!tiendaId && user && user.uid) {
      tiendaId = user.uid;
      source = 'firebase_uid';
      this.logger.debug('[TiendaIdRepository] Fallback usando user.uid como tiendaId');
    }

    // Persistir tiendaId si se encontró
    if (tiendaId) {
      this.persistirTiendaId(tiendaId, source);
      this.logger.info(`[TiendaIdRepository] tiendaId obtenido desde ${source}: ${tiendaId}`);
    } else {
      this.logger.warn('[TiendaIdRepository] ¡CRÍTICO! No se pudo determinar tiendaId de ninguna fuente');
      this._logTiendaIdDiagnostics();
    }

    return { tiendaId, source };
  }

  /**
   * Persiste tiendaId en múltiples almacenamientos para máxima disponibilidad
   * @param {string} tiendaId - ID de tienda a persistir
   * @param {string} source - Fuente de donde se obtuvo el tiendaId
   */
  persistirTiendaId(tiendaId, source) {
    try {
      // Persistir en localStorage para futuras sesiones
      localStorage.setItem(this.STORAGE_KEYS.LOCAL.TIENDA_ID, tiendaId);
      localStorage.setItem(this.STORAGE_KEYS.LOCAL.TIMESTAMP, Date.now().toString());
      localStorage.setItem(this.STORAGE_KEYS.LOCAL.SOURCE, source);
      
      // Persistir en sessionStorage para uso inmediato
      sessionStorage.setItem(this.STORAGE_KEYS.SESSION.TIENDA_ID, tiendaId);
      sessionStorage.setItem(this.STORAGE_KEYS.SESSION.LAST_TIENDA_ID, tiendaId);
      
      this.logger.debug(`[TiendaIdRepository] tiendaId ${tiendaId} persistido exitosamente desde ${source}`);
    } catch (e) {
      this.logger.warn('[TiendaIdRepository] Error al persistir tiendaId:', e);
    }
  }

  /**
   * Obtiene tiendaId desde parámetros URL
   * @private
   * @returns {Object} Resultado con tiendaId y metadata
   */
  _obtenerDesdeUrl() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const tiendaId = urlParams.get('tiendaId') || urlParams.get('tienda_id') || 
                       urlParams.get('store_id') || urlParams.get('shop');
      
      if (tiendaId) {
        this.logger.debug(`[TiendaIdRepository] tiendaId obtenido de parámetros URL: ${tiendaId}`);
        return { tiendaId, metadata: { origin: 'url_params' } };
      }
    } catch (e) {
      this.logger.debug('[TiendaIdRepository] Error al extraer tiendaId de URL:', e);
    }
    
    return { tiendaId: null, metadata: null };
  }

  /**
   * Obtiene tiendaId desde sessionStorage
   * @private
   * @returns {Object} Resultado con tiendaId y metadata
   */
  _obtenerDesdeSessionStorage() {
    try {
      const tiendaId = sessionStorage.getItem(this.STORAGE_KEYS.SESSION.TIENDA_ID) || 
                       sessionStorage.getItem(this.STORAGE_KEYS.SESSION.TIENDA_ID_ALT) || 
                       sessionStorage.getItem(this.STORAGE_KEYS.SESSION.LAST_TIENDA_ID);
      
      if (tiendaId) {
        this.logger.debug(`[TiendaIdRepository] tiendaId recuperado de sessionStorage: ${tiendaId}`);
        return { tiendaId, metadata: { origin: 'session_storage' } };
      }
    } catch (e) {
      this.logger.debug('[TiendaIdRepository] Error al recuperar tiendaId de sessionStorage:', e);
    }
    
    return { tiendaId: null, metadata: null };
  }

  /**
   * Obtiene tiendaId desde localStorage
   * @private
   * @returns {Object} Resultado con tiendaId y metadata
   */
  _obtenerDesdeLocalStorage() {
    try {
      const tiendaId = localStorage.getItem(this.STORAGE_KEYS.LOCAL.TIENDA_ID) || 
                       localStorage.getItem(this.STORAGE_KEYS.LOCAL.TIENDA_ID_ALT);
      
      if (tiendaId) {
        const timestamp = localStorage.getItem(this.STORAGE_KEYS.LOCAL.TIMESTAMP);
        const source = localStorage.getItem(this.STORAGE_KEYS.LOCAL.SOURCE);
        
        this.logger.debug(`[TiendaIdRepository] tiendaId recuperado de localStorage: ${tiendaId} (fuente: ${source})`);
        return { tiendaId, metadata: { timestamp, source } };
      }
    } catch (e) {
      this.logger.debug('[TiendaIdRepository] Error al recuperar tiendaId de localStorage:', e);
    }
    
    return { tiendaId: null, metadata: null };
  }

  /**
   * Extrae tiendaId desde token JWT
   * @private
   * @param {string} token - Token JWT
   * @returns {Object} Resultado con tiendaId y metadata
   */
  _extraerDesdeTiendaIdToken(token) {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) { // Estructura JWT válida
        const tokenPayload = JSON.parse(atob(tokenParts[1]));
        const tiendaId = tokenPayload.tiendaId || tokenPayload.tid || 
                         tokenPayload.store_id || tokenPayload.tienda;
        
        if (tiendaId) {
          this.logger.debug(`[TiendaIdRepository] tiendaId extraído del token JWT: ${tiendaId}`);
          return { tiendaId, metadata: { tokenType: 'JWT', exp: tokenPayload.exp } };
        }
      }
    } catch (e) {
      this.logger.debug('[TiendaIdRepository] Error al extraer tiendaId del token:', e);
    }
    
    return { tiendaId: null, metadata: null };
  }

  /**
   * Extrae tiendaId desde claims de usuario Firebase
   * @private
   * @param {Object} user - Usuario Firebase
   * @returns {Promise<Object>} Resultado con tiendaId y metadata
   */
  async _extraerDesdeClaims(user) {
    try {
      // Intentar extraer de claims ya disponibles
      if (user.claims) {
        const tiendaId = user.claims.tiendaId || user.claims.tid || 
                         user.claims.store_id || user.claims.tienda;
        
        if (tiendaId) {
          this.logger.debug(`[TiendaIdRepository] tiendaId extraído de claims de usuario: ${tiendaId}`);
          return { tiendaId, metadata: { claimType: 'direct' } };
        }
      }
      
      // Intentar obtener claims actualizados desde getIdTokenResult
      if (typeof user.getIdTokenResult === 'function') {
        try {
          const idTokenResult = await user.getIdTokenResult(true);
          if (idTokenResult && idTokenResult.claims) {
            const tiendaId = idTokenResult.claims.tiendaId || idTokenResult.claims.tid || 
                            idTokenResult.claims.store_id || idTokenResult.claims.tienda;
            
            if (tiendaId) {
              this.logger.debug(`[TiendaIdRepository] tiendaId extraído de getIdTokenResult: ${tiendaId}`);
              return { tiendaId, metadata: { claimType: 'token_result', exp: idTokenResult.expirationTime } };
            }
          }
        } catch (e) {
          this.logger.debug('[TiendaIdRepository] Error al obtener idTokenResult:', e);
        }
      }
    } catch (e) {
      this.logger.debug('[TiendaIdRepository] Error al extraer tiendaId de Firebase user:', e);
    }
    
    return { tiendaId: null, metadata: null };
  }

  /**
   * Registra información diagnóstica para ayudar a depurar problemas con tiendaId
   * @private
   */
  _logTiendaIdDiagnostics() {
    try {
      this.logger.debug('[TiendaIdRepository] === DIAGNÓSTICO TIENDA ID ===');
      this.logger.debug(`URL actual: ${window.location.href}`);
      this.logger.debug(`Search params: ${window.location.search}`);
      this.logger.debug(`sessionStorage.tiendaId: ${sessionStorage.getItem(this.STORAGE_KEYS.SESSION.TIENDA_ID)}`);
      this.logger.debug(`sessionStorage.lastTiendaId: ${sessionStorage.getItem(this.STORAGE_KEYS.SESSION.LAST_TIENDA_ID)}`);
      this.logger.debug(`localStorage.tiendaId: ${localStorage.getItem(this.STORAGE_KEYS.LOCAL.TIENDA_ID)}`);
      this.logger.debug(`localStorage.tiendaId_timestamp: ${localStorage.getItem(this.STORAGE_KEYS.LOCAL.TIMESTAMP)}`);
      this.logger.debug(`localStorage.tiendaId_source: ${localStorage.getItem(this.STORAGE_KEYS.LOCAL.SOURCE)}`);
      this.logger.debug('=== FIN DIAGNÓSTICO TIENDA ID ===');
    } catch (e) {
      this.logger.debug('[TiendaIdRepository] Error al generar diagnóstico:', e);
    }
  }
}

export default TiendaIdRepository;
